'use client'

import { ReactNode, useState } from 'react'
import { flushSync } from 'react-dom'
import classNames from 'classnames'
import type { MetaEntityKind, SellerType } from '@/config/metadata-schemas.config'
import { getMissingRequiredSummary } from '@/utils/metadata-form.utils'
import {
  getFieldsMatchingView,
  getMetaFieldElementId,
  isMetaFieldViewActive,
} from '@/utils/metadata-field-view.utils'
import type { MetaFieldFilter } from '@/utils/metadata-field-view.utils'
import { humanizeMetaKey } from '@/utils/metadata-label.utils'
import MetadataResourceForm from './MetadataResourceForm'
import MetadataResourceGroup from './MetadataResourceGroup'
import styles from './MetadataManagementTool.module.css'

type Props = {
  data: MetadataManagementTool.MetadataSnapshot
  /** Which purchase flow the panel is mounted on (admission → exhibition). */
  sellerType: SellerType
}

/** Reference doc explaining what each metadata key does and how it is used. */
const METADATA_DOCS_URL = 'https://docs.superhuman.com/d/_dzb8ANoCnN5/Ticketure-Metadata_suRAVKjL?highlightBlockId=cl-4SLvArQGIw#_luArQGIw'

const FILTERS: Array<{ value: MetaFieldFilter, label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'missing', label: 'Missing' },
  { value: 'invalid', label: 'Invalid' },
  { value: 'set', label: 'Set' },
  { value: 'unset', label: 'Unset' },
]

/**
 * Open every collapsed section around a field, scroll it into view and focus
 * its first input. Fields stay mounted inside closed `<details>`, so the
 * element exists even when its section is collapsed.
 */
const revealField = (elementId: string): void => {
  const field = document.getElementById(elementId)
  if (!field) {
    return
  }
  for (let details = field.closest('details'); details; details = details.parentElement?.closest('details') ?? null) {
    details.open = true
  }
  field.scrollIntoView({ block: 'center' })
  field.querySelector<HTMLElement>('input, select, textarea')?.focus({ preventScroll: true })
}

type Section = {
  title: string
  resourceType: MetaEntityKind
  resourceId: string
}

/**
 * Assemble the FLAT list of resource sections, used to build the missing-required
 * summary at the top (which names each individual tier/group with a gap): the two
 * globals blobs (app config and feature flags) first, then the seller and event
 * (single instances, ids read off their value rows), then one entry per ticket
 * group and ticket type in the roster.
 *
 * The body renders differently — the ticket_group and ticket_type entries are
 * folded into two searchable MetadataResourceGroup containers (see below) rather
 * than one top-level section each.
 */
const buildSections = (data: MetadataManagementTool.MetadataSnapshot): Section[] => {
  const sellerId = data.values.find((value) => value.resourceType === 'seller')?.resourceId
  const eventId = data.values.find((value) => value.resourceType === 'event_template')?.resourceId

  const groups = data.instances.filter((instance) => instance.resourceType === 'ticket_group')
  const types = data.instances.filter((instance) => instance.resourceType === 'ticket_type')

  return [
    { title: 'App config', resourceType: 'app-config', resourceId: 'global' },
    { title: 'Feature flags', resourceType: 'feature-flags', resourceId: 'global' },
    ...(sellerId ? [{ title: 'Seller', resourceType: 'seller' as const, resourceId: sellerId }] : []),
    ...(eventId ? [{ title: 'Event', resourceType: 'event_template' as const, resourceId: eventId }] : []),
    ...groups.map((group) => ({ title: `Ticket group — ${group.name}`, resourceType: 'ticket_group' as const, resourceId: group.id })),
    // Type names repeat across groups, so the summary title carries the group too.
    ...types.map((type) => ({
      title: type.groupName ? `Ticket type — ${type.name} (${type.groupName})` : `Ticket type — ${type.name}`,
      resourceType: 'ticket_type' as const,
      resourceId: type.id,
    })),
  ]
}

/** Sections rendered as their own top-level form (everything except the rostered tiers). */
const isSingleSection = (section: Section): boolean => (
  section.resourceType !== 'ticket_group' && section.resourceType !== 'ticket_type'
)

/**
 * The metadata validation panel. Renders nothing unless the snapshot has data
 * (a non-whitelisted user gets an empty snapshot and so sees nothing).
 * Progressive disclosure: the whole panel is collapsed to a title bar by
 * default (it overlays the flow, so it stays out of the way until asked for),
 * expanding to a summary of missing required fields and then collapsible
 * resource sections (open only when they still have gaps). A search box and
 * filter chips narrow every section to matching fields, and each missing field
 * in the summary jumps straight to its input.
 * Fields are filtered by `sellerType` via schema `x-appliesTo` so events-only
 * metadata never appears on the admission (exhibition) panel.
 */
const MetadataManagementTool = ({ data, sellerType }: Props): ReactNode => {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<MetaFieldFilter>('all')

  if (!data.values.length) {
    return null
  }

  const sections = buildSections(data)
  const missingSummary = getMissingRequiredSummary(sections, data.values, sellerType)

  const missingCount = missingSummary.reduce((total, item) => total + item.fieldKeys.length, 0)

  const singleSections = sections.filter(isSingleSection)
  const ticketGroups = data.instances.filter((instance) => instance.resourceType === 'ticket_group')
  const ticketTypes = data.instances.filter((instance) => instance.resourceType === 'ticket_type')

  const view = { query, filter }
  const hasNoMatches = isMetaFieldViewActive(view) && !sections.some((section) => (
    getFieldsMatchingView(section.resourceType, section.resourceId, data.values, sellerType, view).length > 0
  ))

  // Clear the search/filter synchronously so a hidden field is back in the DOM before we scroll to it.
  const jumpToField = (elementId: string): void => {
    flushSync(() => {
      setQuery('')
      setFilter('all')
    })
    revealField(elementId)
  }

  return (
    <details className={styles.Panel} data-testid="metadata-management-tool" data-seller-type={sellerType}>
      <summary className={styles.PanelHeader}>
        <h2 className={styles.PanelTitle}>Metadata Management</h2>
        {missingCount > 0
          ? (
            <span className={styles.ResourceBadge} data-testid="metadata-panel-missing-count">
              {missingCount} required
            </span>
          )
          : (
            <span className={styles.ResourceBadgeOk} data-testid="metadata-panel-complete">
              complete
            </span>
          )}
      </summary>
      <div className={styles.PanelBody}>
        <a
          className={styles.DocsLink}
          href={METADATA_DOCS_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="metadata-docs-link"
        >
          Metadata documentation ↗
        </a>
        {missingSummary.length > 0
          ? (
            <div className={styles.MissingSummary} data-testid="metadata-missing-summary">
              <p className={styles.MissingSummaryTitle}>Missing required</p>
              <ul className={styles.MissingSummaryList}>
                {missingSummary.map((item) => (
                  <li key={`${item.resourceType}:${item.resourceId}`}>
                    <strong>{item.sectionTitle}</strong>
                    {': '}
                    {item.fieldKeys.map((key, index) => (
                      <span key={key}>
                        {index > 0 && ', '}
                        <button
                          type="button"
                          className={styles.SummaryLink}
                          onClick={() => jumpToField(getMetaFieldElementId(item.resourceType, item.resourceId, key))}
                          data-testid={`metadata-summary-link-${item.resourceType}-${item.resourceId}-${key}`}
                        >
                          {humanizeMetaKey(key)}
                        </button>
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          )
          : (
            <p className={styles.AllComplete} data-testid="metadata-all-complete">
              All required metadata is set
            </p>
          )}
        <div className={styles.Toolbar}>
          <input
            type="search"
            className={styles.ToolbarSearch}
            placeholder="Search fields…"
            aria-label="Search fields"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            data-testid="metadata-field-search"
          />
          <div className={styles.FilterChips} role="group" aria-label="Filter fields">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={classNames(styles.FilterChip, { [styles.FilterChipActive]: filter === option.value })}
                aria-pressed={filter === option.value}
                onClick={() => setFilter(option.value)}
                data-testid={`metadata-filter-${option.value}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {hasNoMatches && (
          <p className={styles.GroupEmpty} data-testid="metadata-no-matches">
            No fields match
          </p>
        )}
        {singleSections.map((section) => (
          <MetadataResourceForm
            key={`${section.resourceType}:${section.resourceId}`}
            title={section.title}
            resourceType={section.resourceType}
            resourceId={section.resourceId}
            values={data.values}
            sellerType={sellerType}
            view={view}
          />
        ))}
        {ticketGroups.length > 0 && (
          <MetadataResourceGroup
            title="Ticket groups"
            resourceType="ticket_group"
            instances={ticketGroups}
            values={data.values}
            sellerType={sellerType}
            view={view}
          />
        )}
        {ticketTypes.length > 0 && (
          <MetadataResourceGroup
            title="Ticket types"
            resourceType="ticket_type"
            instances={ticketTypes}
            values={data.values}
            sellerType={sellerType}
            view={view}
          />
        )}
      </div>
    </details>
  )
}

export default MetadataManagementTool
